import {
  Box,
  FormField,
  Popover,
  Select,
  StatusIndicator,
} from '@cloudscape-design/components';
import useRealtimeOrgUsers from 'features/user/hooks/useRealtimeOrgUsers';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import AddUserModal from './addUserModal';
import DisplayUserEmailById from './displayUserEmailById';

export default function UserSelectByOrg({
  organization = null,
  selectedUserId = null,
  onUserChange,
  canAddUser = false,
  label = 'User',
  errorText = '',
}) {
  const [openAddUserModal, setOpenAddUserModal] = useState(false);
  const organizationId = organization?.id || null;
  // Joined to a string so the realtime query doesn't resubscribe on every new array
  const organizationDomainsStr = organization?.domains?.join(',') || '';

  const { data: orgUsers } = useRealtimeOrgUsers({
    orgId: organizationId,
    domainsStr: organizationDomainsStr,
  });

  const userOptions = useMemo(() => {
    if (!orgUsers?.length) return [];
    const usersInTheOrgGroup = [];
    const usersInTheOrgDomainGroup = [];
    orgUsers.forEach((user) => {
      if (user.organization === organizationId) {
        usersInTheOrgGroup.push({
          value: user.id,
          data: user,
          label: user.email,
        });
      } else {
        usersInTheOrgDomainGroup.push({
          value: user.id,
          data: user,
          label: user.email,
        });
      }
    });
    return [
      ...(usersInTheOrgGroup.length ? [
        {
          label: 'Users who belong to this organization',
          options: usersInTheOrgGroup,
        },
      ] : []),
      ...(usersInTheOrgDomainGroup.length ? [
        {
          label: 'Users who share the same email domain as this organization but are not members',
          options: usersInTheOrgDomainGroup,
        }] : []),
    ];
  }, [orgUsers, organizationId]);

  const selectedUser = useMemo(
    () => userOptions
      .flatMap((group) => group.options)
      .find((user) => user.value === selectedUserId),
    [userOptions, selectedUserId],
  );

  const lastNotifiedUserId = useRef(null);

  const handleUserChange = useCallback((userData) => {
    const userId = userData?.id || null;
    if (lastNotifiedUserId.current === userId) return;
    lastNotifiedUserId.current = userId;
    onUserChange(userData);
  }, [onUserChange]);

  useEffect(() => {
    if (lastNotifiedUserId.current === selectedUserId) return;
    if (!selectedUser?.data) return;
    handleUserChange(selectedUser.data);
  }, [selectedUser, handleUserChange, selectedUserId]);

  return (
    <>
      <FormField
        label={label}
        errorText={errorText}
        // Show user email in description if user is selected but email is not in the options
        // (can happen when org changes and the selected user doesn't belong to the new org)
        description={selectedUserId && !selectedUser && (
          <DisplayUserEmailById userId={selectedUserId} />
        )}
        constraintText={canAddUser && (
          <span>
            {'Don\'t see the user? '}
            {organizationId ? (
              <a href="#!" onClick={(e) => { e.preventDefault(); setOpenAddUserModal(true); }}>Add here</a>
            ) : (
              // Same link look, but clicking explains why instead of opening the modal
              <Popover triggerType="custom" size="small" content={<StatusIndicator type="error">Select an organization first</StatusIndicator>}>
                {/* Popover's trigger wrapper resets to body font; match constraint text */}
                <Box display="inline" fontSize="body-s">
                  <a href="#!" onClick={(e) => e.preventDefault()}>Add here</a>
                </Box>
              </Popover>
            )}
          </span>
        )}
        stretch
      >
        <Select
          filteringType="auto"
          selectedOption={selectedUser || null}
          options={userOptions}
          onChange={({ detail }) => {
            handleUserChange(detail.selectedOption?.data || null);
          }}
        />
      </FormField>
      {canAddUser && openAddUserModal && organization && (
        <AddUserModal
          open={openAddUserModal}
          organization={organization}
          onClose={() => setOpenAddUserModal(false)}
          onUserAdded={(newUser) => {
            handleUserChange(newUser);
            setOpenAddUserModal(false);
          }}
        />
      )}
    </>
  );
}

UserSelectByOrg.propTypes = {
  organization: PropTypes.shape({
    id: PropTypes.string.isRequired,
    domains: PropTypes.arrayOf(PropTypes.string),
  }),
  selectedUserId: PropTypes.string,
  onUserChange: PropTypes.func.isRequired,
  errorText: PropTypes.string,
  label: PropTypes.string,
  canAddUser: PropTypes.bool,
};
